import React, { useState } from 'react';
import axios from 'axios';
import { Helmet } from 'react-helmet-async';
import LocationAutocomplete from './LocationAutocomplete';

function NatalOracle({ session, signLibrary, houseLibrary, somaticLibrary }) {
  const [formData, setFormData] = useState({
    client_name: '', year: 1990, month: 1, day: 1, time: "12:00", lat: 37.77, lon_geo: -122.41, utcOffset: 0
  });
  
  const [loading, setLoading] = useState(false);
  const [report, setReport] = useState(null);

  // The God-Mode Firewall
  if (session?.user?.email !== 'eduardoadonis@gmail.com') {
    return (
      <div style={{ textAlign: 'center', padding: '100px 20px', color: '#ffca28', minHeight: '60vh' }}>
        <h2 style={{ textTransform: 'uppercase', letterSpacing: '2px' }}>Vault Locked</h2>
        <p style={{ color: '#8b949e', marginTop: '15px' }}>This terminal is restricted to the Lead Architect.</p>
      </div>
    );
  }

  const calculateUtcHour = (timeStr, offset) => {
    const [hours, minutes] = timeStr.split(':').map(Number);
    const localDecimalHour = hours + (minutes / 60);
    return localDecimalHour - offset;
  };

  const handleGenerate = async (e) => {
    e.preventDefault();
    setLoading(true);
    setReport(null);
    
    try {
      const finalUtc = calculateUtcHour(formData.time, formData.utcOffset);
      const chartRes = await axios.get('/api/calculate', { params: {
        year: formData.year, month: formData.month, day: formData.day,
        hour_utc: finalUtc, lat: formData.lat, lon_geo: formData.lon_geo
      }});

      // Compile the Supabase text for the Python engine
      const PLANET_ORDER = ["Rising Sign", "Sun", "Moon", "Mercury", "Venus", "Mars", "Jupiter", "Saturn", "Uranus", "Neptune", "Pluto", "Ceres", "Chiron"];
      const compiledScripture = [];
      
      PLANET_ORDER.forEach(body => {
        const data = chartRes.data[body];
        if (!data) return;
        
        const lookupBody = body === "Rising Sign" ? "Rising" : body.trim();
        const safeSign = data.sign ? data.sign.trim() : "";
        const safeHouse = data.house ? String(data.house).trim() : "";
        const signKey = `${lookupBody}_${safeSign}`;
        const houseKey = `${lookupBody}_${safeHouse}`;
        
        compiledScripture.push({
            body: body,
            sign: safeSign,
            house: safeHouse,
            sign_text: signLibrary[signKey] || `Spirit channeling for ${signKey} in progress...`,
            house_text: houseLibrary[houseKey] || `House alignment for ${houseKey} pending...`,
            somatic_text: somaticLibrary[signKey] || `Somatic protocol for ${signKey} pending...`
        });
      });

      const natalPayload = {
        client_name: formData.client_name || "Initiate",
        client_chart: chartRes.data,
        compiled_scripture: compiledScripture 
      };

      const response = await axios.post('/api/natal_oracle', natalPayload);
      setReport(response.data);

    } catch (err) {
      console.error("Oracle Engine misaligned:", err);
      const errorMsg = err.response?.data?.error || err.message;
      alert(`Matrix glitch: ${errorMsg}`);
    } finally {
      setLoading(false);
    }
  };
  
  const renderInput = (label, field, type="text") => (
    <div style={{ marginBottom: '15px' }}>
      <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '5px' }}>{label}</label>
      <input 
        type={type} 
        value={formData[field]} 
        onChange={(e) => setFormData({...formData, [field]: e.target.value})}
        style={{ width: '100%', padding: '10px', background: '#05070a', border: '1px solid #30363d', color: 'white', borderRadius: '4px', boxSizing: 'border-box', colorScheme: 'dark' }}
      />
    </div>
  );

  return (
    <div style={{ maxWidth: '800px', margin: '40px auto', padding: '0 20px' }}>
      <Helmet><title>Natal Oracle Engine | Adonisian Matrix</title></Helmet>

      <div style={{ textAlign: 'center', marginBottom: '40px' }}>
        <h2 style={{ color: '#00d4ff', fontSize: '2rem', letterSpacing: '2px', textTransform: 'uppercase', margin: '0 0 10px 0' }}>👁️ Master Natal Oracle</h2>
        <p style={{ color: '#ffca28', fontSize: '1rem', fontStyle: 'italic', margin: 0 }}>Solo-Subject Matrix Compilation & Vault Dispatch</p>
      </div>

      <div style={{ background: 'rgba(0, 212, 255, 0.03)', border: '1px solid #00d4ff', padding: '30px', borderRadius: '8px', maxWidth: '600px', margin: '0 auto 30px auto' }}>
        <h3 style={{ color: '#ffffff', marginTop: 0, borderBottom: '1px solid #30363d', paddingBottom: '10px', letterSpacing: '1px' }}>Client Telemetry</h3>
        
        {renderInput("Client Name", "client_name")}
        
        <div style={{ display: 'flex', gap: '10px' }}>
          {renderInput("Year", "year", "number")}
          {renderInput("Month", "month", "number")}
          {renderInput("Day", "day", "number")}
        </div>
        
        {renderInput("Exact Local Time", "time", "time")}
        
        <div style={{ marginBottom: '15px' }}>
          <label style={{ display: 'block', color: '#8b949e', fontSize: '0.85rem', marginBottom: '5px' }}>Birth Location</label>
          <LocationAutocomplete 
            onLocationCalculated={(loc) => setFormData(prev => ({
              ...prev, lat: loc.lat, lon_geo: loc.lng, utcOffset: loc.utcOffset 
            }))} 
          />
        </div>
      </div>

      <div style={{ textAlign: 'center' }}>
        <button 
          onClick={handleGenerate} disabled={loading}
          style={{ backgroundColor: loading ? '#30363d' : '#00d4ff', color: loading ? '#8b949e' : '#0d1117', border: 'none', padding: '15px 40px', fontSize: '1.2rem', fontWeight: 'bold', textTransform: 'uppercase', borderRadius: '4px', cursor: loading ? 'not-allowed' : 'pointer', boxShadow: '0 0 15px rgba(0, 212, 255, 0.2)' }}
        >
          {loading ? "⚡ Compiling Core Architecture..." : "Generate Solo Blueprint"}
        </button>
      </div>

      {report && (
        <div style={{ marginTop: '50px', background: '#05070a', border: '1px dashed #00d4ff', padding: '30px', borderRadius: '8px' }}>
          <h3 style={{ color: '#ffffff', letterSpacing: '1px', borderBottom: '1px solid #30363d', paddingBottom: '10px' }}>✅ Blueprint Forged: {formData.client_name}</h3>
          <p style={{ color: '#ffca28', fontStyle: 'italic', fontSize: '0.9rem' }}>The Master Dossier and Client Scripture have been secured in eduardoadonis@gmail.com.</p>
          <h4 style={{ color: '#00d4ff', marginTop: '20px' }}>Internal Somatic Nodes:</h4>
          <ul style={{ color: '#c9d1d9', lineHeight: '1.8' }}>
            {report.aspects.map((aspect, i) => <li key={i}>{aspect}</li>)}
          </ul>
        </div>
      )}
    </div>
  );
}

export default NatalOracle;